import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter } from 'rxjs';
import { ReleaseHighlight, latestRelease } from '../../data-models/release-notes.model';
import { FeatureFlagsState } from '../../../store/feature-flags/feature-flags.state';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { Internal } from '../../decorators/app.decorators';

export const WHATS_NEW_SEEN_KEY = 'pcs-whats-new-seen';
export const WHATS_NEW_VISITED_KEY = 'pcs-whats-new-visited';
export const WHATS_NEW_HIDDEN_KEY = 'pcs-whats-new-hidden';

/**
 * State for the newest release's highlights: the dashboard "What's new" card, the Release
 * Notes dot and the "New" nav tags. Each localStorage key holds the releaseDate it was saved
 * for, so everything resets when a newer release is added. Keys survive logout on purpose.
 */
@Internal()
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly featureFlags = inject(Store).selectSignal(FeatureFlagsState.getFeatureFlags);
  private readonly release = latestRelease();
  private readonly releaseDate = this.release?.releaseDate ?? '';
  private readonly allHighlights = this.release?.highlights ?? [];

  /** Release notes opened since this release shipped; clears the Release Notes dot. */
  readonly notesSeen = signal(readStorage(WHATS_NEW_SEEN_KEY) === this.releaseDate);
  private readonly hidden = signal(readStorage(WHATS_NEW_HIDDEN_KEY) === this.releaseDate);
  private readonly visited = signal<ReadonlySet<string>>(this.loadVisited());

  /** Rows the user can use: flag allowed, and not dropped after a visit. */
  readonly highlights = computed<ReleaseHighlight[]>(() => {
    this.featureFlags(); // isFeatureFlagEnabled reads a snapshot; touch the signal so late flags re-run this
    const visited = this.visited();
    return this.allHighlights.filter(h =>
      (!h.flag || this.featureFlagService.isFeatureFlagEnabled(h.flag)) &&
      !(h.hideAfterVisit && h.route && visited.has(h.route)));
  });

  readonly cardVisible = computed(() => !this.hidden() && this.highlights().length > 0);

  constructor() {
    const router = inject(Router);
    if (router.navigated) {
      this.onNavigated(router.url);
    }
    router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.onNavigated(e.urlAfterRedirects));
  }

  /** Nav tabs are already hidden by their own flags, so only the visit matters here. */
  showTabTag(route: string): boolean {
    return this.allHighlights.some(h => h.route === route) && !this.visited().has(route);
  }

  markNotesSeen(): void {
    this.notesSeen.set(true);
    writeStorage(WHATS_NEW_SEEN_KEY, this.releaseDate);
  }

  hideCard(): void {
    this.hidden.set(true);
    writeStorage(WHATS_NEW_HIDDEN_KEY, this.releaseDate);
  }

  private onNavigated(url: string): void {
    const path = url.split(/[?#]/)[0];
    const route = this.allHighlights.find(h => h.route && (path === h.route || path.startsWith(h.route + '/')))?.route;
    if (!route || this.visited().has(route)) {
      return;
    }
    const next = new Set(this.visited()).add(route);
    this.visited.set(next);
    writeStorage(WHATS_NEW_VISITED_KEY, JSON.stringify({ releaseDate: this.releaseDate, routes: [...next] }));
  }

  private loadVisited(): Set<string> {
    try {
      const saved = JSON.parse(readStorage(WHATS_NEW_VISITED_KEY) ?? 'null');
      if (saved?.releaseDate === this.releaseDate && Array.isArray(saved.routes)) {
        return new Set(saved.routes);
      }
    } catch {
      // Corrupt value; start fresh.
    }
    return new Set();
  }
}

// localStorage throws in Safari private mode and some webviews; fail open (show things again).
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked; state is kept for this session only.
  }
}
